import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { CartItemsComponent } from '@theme/component-cart-items';

const integer = (value, fallback = null) => {
  if (value === '' || value == null) return fallback;
  const result = Number(value);
  return Number.isSafeInteger(result) && result >= 0 ? result : null;
};
const variantId = (value) => /^\d+$/.test(String(value ?? '')) && Number(value) > 0 ? String(value) : null;
const productId = (value) => String(value ?? '').replace(/^gid:\/\/shopify\/Product\//, '');
const cartQuantity = (cart, id) => cart.items.reduce((sum, item) =>
  String(item.variant_id ?? item.id) === id ? sum + (integer(item.quantity, 0) || 0) : sum, 0);
const optionImage = (value) => {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.href);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};
const eligibleOption = (option) => {
  if (!option || option.disabled || option.dataset.bundleIneligible === 'true') return false;
  const price = integer(option.dataset.price);
  const min = integer(option.dataset.quantity, 1);
  const step = integer(option.dataset.step, 1);
  const max = integer(option.dataset.max);
  return price > 0 && min > 0 && step > 0 && (max == null || max >= min) &&
    (!option.dataset.max || max != null);
};

class LevayoBundle extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.variantGeneration = (this.variantGeneration || 0) + 1;
    this.button = this.querySelector('.lv-bundle__submit');
    this.status = this.querySelector('[data-bundle-status]');
    this.total = this.querySelector('[data-bundle-total]');
    this.cartLink = this.querySelector('[data-bundle-cart-link]');
    if (!this.button || !this.status || !this.total) return;
    try {
      this.format = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'de-DE', {
        style: 'currency', currency: this.dataset.currency || 'EUR',
      });
    } catch {
      this.format = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
    }
    this.addEventListener('change', this.handleChange, { signal: this.abort.signal });
    this.button.addEventListener('click', this.handleAdd, { signal: this.abort.signal });
    document.addEventListener(StandardEvents.productSelect, this.handleProductSelect, { signal: this.abort.signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleExternalCartMutation, { signal: this.abort.signal });
    this.update();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.variantGeneration += 1;
  }

  message(name, replacements = {}) {
    let result = this.dataset[`message${name}`] || '';
    for (const [key, value] of Object.entries(replacements)) {
      result = result.replaceAll(`__${key}__`, String(value));
    }
    return result;
  }

  cards() {
    return Array.from(this.querySelectorAll('[data-bundle-card]'));
  }

  primarySelected() {
    const primary = this.cards().find((card) => card.hasAttribute('data-primary'));
    const check = primary?.querySelector('[data-bundle-check]');
    return Boolean(check?.checked && !check.disabled);
  }

  selection() {
    return this.cards().flatMap((card) => {
      const check = card.querySelector('[data-bundle-check]');
      const select = card.querySelector('[data-bundle-variant]');
      const option = Array.from(select?.options || []).find((candidate) => candidate.value === select.value);
      const id = variantId(option?.value);
      const min = integer(option?.dataset.quantity, 1);
      const step = integer(option?.dataset.step, 1);
      const price = integer(option?.dataset.price);
      if (!check?.checked || check.disabled || !eligibleOption(option) || !id) return [];
      return [{
        id, quantity: min, min, step, max: integer(option.dataset.max), price,
        title: card.querySelector('.lv-bundle__title')?.textContent?.trim() || '',
      }];
    });
  }

  update() {
    if (!this.format || !this.button) return;
    for (const card of this.cards()) {
      const select = card.querySelector('[data-bundle-variant]');
      const option = Array.from(select?.options || []).find((candidate) => candidate.value === select.value);
      const check = card.querySelector('[data-bundle-check]');
      const price = integer(option?.dataset.price);
      const min = integer(option?.dataset.quantity, 1) || 1;
      const valid = eligibleOption(option) && Boolean(variantId(option?.value));
      if (check && !this.busy) {
        check.disabled = !valid || card.hasAttribute('data-bundle-unsupported') || Boolean(card.querySelector('[data-bundle-unsupported]'));
      }
      const priceNode = card.querySelector('[data-bundle-price]');
      if (priceNode) priceNode.textContent = price == null ? '—' : this.format.format(price * min / 100);
      const quantityLabel = card.querySelector('[data-bundle-quantity-label]');
      if (quantityLabel) {
        quantityLabel.textContent = (quantityLabel.dataset.template || '').replaceAll('__quantity__', String(min));
        quantityLabel.hidden = min <= 1;
      }
      const image = card.querySelector('.lv-bundle__image img');
      const source = optionImage(option?.dataset.image);
      if (image && source) {
        image.removeAttribute('srcset');
        image.src = source;
      }
      const swatch = card.querySelector('[data-bundle-swatch]');
      if (swatch && /^#[0-9a-f]{3,8}$/i.test(option?.dataset.swatch || '')) swatch.style.backgroundColor = option.dataset.swatch;
      const titleLink = card.querySelector('a.lv-bundle__title');
      if (titleLink && variantId(option?.value)) {
        try {
          const url = new URL(option.dataset.url || titleLink.getAttribute('href') || '', window.location.href);
          if (url.origin === window.location.origin && ['https:', 'http:'].includes(url.protocol)) {
            url.searchParams.set('variant', option.value);
            titleLink.href = url.href;
          }
        } catch {
          continue;
        }
      }
    }
    const items = this.busy ? this.pendingSelection || [] : this.selection();
    this.total.textContent = this.format.format(items.reduce((sum, item) => sum + item.price * item.quantity, 0) / 100);
    const variantBlocked = this.primarySelected() && (this.variantPending || this.variantUnavailable);
    const blocked = Boolean(this.busy || this.uncertain || variantBlocked || !items.length || this.dataset.demo === 'true');
    this.button.disabled = blocked && !this.busy;
    this.button.setAttribute('aria-disabled', String(blocked));
    this.button.setAttribute('aria-busy', this.busy ? 'true' : 'false');
    this.button.textContent = this.busy ? this.message('Loading') : this.message('Add', { count: items.length });
    this.setAttribute('aria-busy', this.busy ? 'true' : 'false');
  }

  handleChange = (event) => {
    if (this.busy || !(event.target instanceof Element) || !event.target.closest('[data-bundle-card]')) return;
    if (event.target.matches('[data-bundle-variant]') && event.target.closest('[data-primary]')) {
      this.variantGeneration += 1;
      this.variantPending = false;
      this.variantUnavailable = false;
    }
    if (!this.uncertain) this.status.textContent = '';
    this.update();
  };

  handleAdd = () => this.add();

  handleExternalCartMutation = (event) => {
    if (event.target !== this) this.cartGeneration = (this.cartGeneration || 0) + 1;
  };

  handleProductSelect = async (event) => {
    const source = event.target;
    if (!(source instanceof Element) || source.closest('product-card, quick-add-dialog, quick-add-component, featured-product-information, dialog')) return;
    const picker = source.closest('variant-picker');
    if (!picker || picker.dataset.templateProductMatch !== 'true') return;
    const selectedProduct = event.product?.id || source.closest('[data-product-id]')?.dataset.productId;
    if (productId(selectedProduct) !== productId(this.dataset.productId) || !event.promise) return;
    const primary = this.cards().find((card) => card.hasAttribute('data-primary'));
    if (!primary) return;
    const generation = ++this.variantGeneration;
    this.variantPending = true;
    this.update();
    try {
      const result = await event.promise;
      if (this.activeOperation) await this.activeOperation;
      if (!this.isConnected || generation !== this.variantGeneration) return;
      const detail = result?.detail;
      if (detail?.productId && productId(detail.productId) !== productId(this.dataset.productId)) return;
      const resource = detail?.resource;
      const id = variantId(resource?.id);
      const price = integer(resource?.price);
      const select = primary.querySelector('[data-bundle-variant]');
      if (!select || !id || !(price > 0) || resource.available !== true || resource.requires_selling_plan === true) {
        this.variantUnavailable = true;
        this.status.textContent = this.message('Unavailable');
        return;
      }
      let option = Array.from(select.options).find((candidate) => candidate.value === id);
      const freshOption = detail.html?.querySelector(`levayo-bundle[data-product-id="${productId(this.dataset.productId)}"] [data-primary] [data-bundle-variant] option[value="${id}"]`);
      const eligibilitySource = freshOption || option;
      const confirmedNewOption = freshOption && ['price', 'quantity', 'max', 'step'].every((key) => freshOption.dataset[key] != null);
      if (!eligibleOption(eligibilitySource) || (!option && !confirmedNewOption)) {
        this.variantUnavailable = true;
        this.status.textContent = this.message('Unavailable');
        return;
      }
      const quantityInput = detail.html?.querySelector(`product-form-component[data-product-id="${productId(this.dataset.productId)}"] [ref="quantityInput"]`);
      const min = integer(quantityInput?.min ?? resource.quantity_rule?.min ?? eligibilitySource.dataset.quantity, 1);
      const maxValue = quantityInput?.max ?? resource.quantity_rule?.max ?? eligibilitySource.dataset.max;
      const max = integer(maxValue);
      const step = integer(quantityInput?.step ?? resource.quantity_rule?.increment ?? eligibilitySource.dataset.step, 1);
      if (!(min > 0) || !(step > 0) || (max != null && max < min) || (maxValue != null && maxValue !== '' && max == null)) {
        this.variantUnavailable = true;
        this.status.textContent = this.message('Unavailable');
        return;
      }
      if (!option) {
        option = document.createElement('option');
        option.value = id;
        option.textContent = resource.public_title || resource.title || '';
        select.append(option);
      }
      option.dataset.price = String(price);
      option.dataset.quantity = String(min);
      option.dataset.max = String(max ?? '');
      option.dataset.step = String(step);
      if (freshOption?.dataset.url) option.dataset.url = freshOption.dataset.url;
      if (freshOption?.dataset.swatch) option.dataset.swatch = freshOption.dataset.swatch;
      if (freshOption) option.dataset.bundleIneligible = freshOption.dataset.bundleIneligible || 'false';
      const image = resource.featured_image?.src || resource.featured_media?.preview_image?.src;
      if (optionImage(image)) option.dataset.image = image;
      option.disabled = false;
      select.value = id;
      this.variantUnavailable = false;
      if (!this.uncertain) this.status.textContent = '';
    } catch {
      if (this.isConnected && generation === this.variantGeneration) {
        this.variantUnavailable = true;
        this.status.textContent = this.message('Unavailable');
      }
    } finally {
      if (generation === this.variantGeneration) {
        this.variantPending = false;
        this.update();
      }
    }
  };

  async waitForCartQuantities() {
    let pending = CartItemsComponent.pendingQuantityUpdates;
    let waited = false;
    while (pending.length) {
      waited = true;
      await Promise.all(pending);
      pending = CartItemsComponent.pendingQuantityUpdates;
    }
    return waited;
  }

  cartUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart.js`;
  }

  async readCart() {
    const response = await fetch(this.cartUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Cart unavailable');
    const cart = await response.json();
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart');
    return cart;
  }

  freezeControls() {
    const controls = Array.from(this.querySelectorAll('input, select, button')).map((control) => [control, control.disabled]);
    for (const [control] of controls) {
      if (control !== this.button) control.disabled = true;
    }
    return () => {
      for (const [control, disabled] of controls) control.disabled = disabled;
    };
  }

  markUncertain() {
    this.uncertain = true;
    this.status.textContent = this.message('Uncertain') || this.message('Error');
    if (this.cartLink) this.cartLink.hidden = false;
  }

  publishCart(cart, items, sections, didError) {
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections, source: 'levayo-bundle', sourceId: this.id, didError },
      }),
    }));
  }

  async add() {
    if (this.busy || this.uncertain || (this.primarySelected() && (this.variantPending || this.variantUnavailable)) || this.dataset.demo === 'true') return;
    const selected = this.selection();
    if (!selected.length) {
      this.status.textContent = this.message('NoSelection');
      return;
    }
    this.busy = true;
    this.pendingSelection = selected;
    this.status.textContent = this.message('Loading');
    this.update();
    const restore = this.freezeControls();
    this.activeOperation = this.performAdd(selected);
    try {
      await this.activeOperation;
    } finally {
      this.activeOperation = null;
      this.pendingSelection = null;
      restore();
      this.busy = false;
      this.update();
    }
  }

  async performAdd(selected) {
    let before;
    const items = [];
    for (const selection of selected) {
      const existing = items.find((item) => item.id === selection.id);
      if (existing) existing.quantity += selection.quantity;
      else items.push({ id: selection.id, quantity: selection.quantity });
    }
    try {
      await this.waitForCartQuantities();
      before = await this.readCart();
      for (const selection of selected) {
        const item = items.find((candidate) => candidate.id === selection.id);
        const existing = cartQuantity(before, selection.id);
        if (!item || selection.quantity < selection.min || (selection.quantity - selection.min) % selection.step !== 0 ||
          (selection.max != null && existing + item.quantity > selection.max)) {
          this.status.textContent = this.message('Quantity', { product: selection.title, maximum: selection.max ?? '—' }) || this.message('Unavailable');
          return;
        }
      }
    } catch {
      this.status.textContent = this.message('Error');
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component'), (item) => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
    let response;
    let result;
    let requestError = false;
    const cartGeneration = this.cartGeneration || 0;
    try {
      response = await fetch(globalThis.Theme?.routes?.cart_add_url || `${root}cart/add.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });
      result = await response.json();
    } catch {
      requestError = true;
    }

    try {
      const waitedForQuantity = await this.waitForCartQuantities();
      const after = await this.readCart();
      const hadConcurrentQuantityUpdate = waitedForQuantity || cartGeneration !== (this.cartGeneration || 0);
      const added = items.every((item) => cartQuantity(after, item.id) - cartQuantity(before, item.id) >= item.quantity);
      const changed = items.some((item) => cartQuantity(after, item.id) !== cartQuantity(before, item.id));
      const succeeded = added && !hadConcurrentQuantityUpdate;
      const safeSections = response?.ok && !result?.status && !hadConcurrentQuantityUpdate ? result?.sections : undefined;
      this.publishCart(after, items, safeSections, !succeeded);
      if (succeeded) {
        this.status.textContent = this.message('Success');
      } else if (!requestError && response?.status === 422 && result?.status && !changed) {
        this.status.textContent = typeof result.description === 'string' ? result.description : this.message('Unavailable');
      } else {
        this.markUncertain();
      }
    } catch {
      this.markUncertain();
    }
  }
}

if (!customElements.get('levayo-bundle')) customElements.define('levayo-bundle', LevayoBundle);
